<template>
  <div class="hr-field-view">
    <span class="hr-field-view__title" :data-testid="testIdLabel">
      {{ state.theField.label }}
    </span>
    <span
      class="hr-field-view__value fs-mask"
      @click="toggleMask()"
      :data-testid="testIdValue"
    >
      {{
        !state.theField.validation?.sensitive ||
        state.viewSensitive ||
        isSensitiveHidden ||
        owner
          ? formattedValue
          : sensitiveMasking
      }}
    </span>
    <template v-if="!isSensitiveHidden">
      <co-tooltip
        v-if="state.theField.validation?.sensitive && !owner && !isPortalUser"
        placement="top"
      >
        <template #content>
          <div>
            {{
              state.viewSensitive
                ? t('hr-field-view:mask')
                : t('hr-field-view:unmask')
            }}
          </div>
        </template>
        <co-icon
          class="hr-field-view__value-view"
          :name="!state.viewSensitive ? 'eye-solid' : 'eye-solid-closed'"
          data-test-id="hr-co-icon-eye"
          @click="toggleMask()"
        />
      </co-tooltip>
    </template>
  </div>
</template>

<script>
import { reactive, computed } from 'vue';
import { getTypeValidator, typeTranslation } from '@/core/input-types/utils';
import countries from '@/core/country.json';
import { CoIcon, CoTooltip } from '@deputy/copilot2';
import t from '@/lib/i18next';
import { SUPERANNUATION, TAX_DETAILS } from '@/core/field-names';
import { checkSignatureStatus } from '@/lib/check-signature-status';

export default {
  // move whole existing hr-field-view as hr-field-default-view.
  name: 'hr-field-default-view',
  components: { CoIcon, CoTooltip },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    owner: {
      type: Boolean,
      default: false,
    },
    isSensitiveHidden: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const state = reactive({
      theField: props.value,
      rules: {
        visible: !props.value.hide,
      },
      viewSensitive: false,
    });

    const testIdLabel = computed(() => {
      return `hr-field-view-title-${state.theField.name}`;
    });
    const testIdValue = computed(() => {
      return `hr-field-view-value-${state.theField.name}`;
    });

    const isPortalUser = window?.__APP__?.DEBUG_REMOVE['dp.pu'];

    const countriesFrozen = Object.freeze(countries);

    const shouldShow = computed(
      () => state.theField.type !== 'CONTENT' && state.rules.visible
    );

    const formattedValue = computed(() => {
      const isRequiredField = state.theField.validation?.required;
      if (state.theField.validation && state.theField.validation.toPrint) {
        return state.theField.validation.toPrint(state.theField);
      }

      if (state.theField.type === 'RADIO') {
        return (
          state.theField.children.find(
            (child) => child.value === state.theField.value
          ) || { label: '' }
        ).label;
      }

      if (state.theField.type === 'CHECK') {
        return t('hr-field-view:checked');
      }

      if (
        state.theField.name === 'tfnDeclaration.taxDetails.countryOfOrigin' ||
        state.theField.name === 'workRights.countryOfDocument'
      ) {
        if (!state.rules.visible || !state.theField.value) {
          return t('hr-field-view:not-provided');
        } else {
          const { country } = countriesFrozen.find(
            (country) => country.code === state.theField.value
          );
          return country;
        }
      }

      // TODO: fix fileupload logic and remove this if case
      if (state.theField.type === 'FILE_UPLOAD') {
        // TODO: add elvis operator and get rid of this weird code
        const notUploadedTextFields = [
          TAX_DETAILS.P45_UPLOAD,
          SUPERANNUATION.SELF_MANAGED_FORM,
        ];

        const notUploadedText = !notUploadedTextFields.includes(
          state.theField.name
        )
          ? t('hr-field-view:not-uploaded')
          : t('hr-field-view:not-provided');

        const SUPER_CHOICE_FORM_LABEL = 'Super Choice form';

        const fileSize = parseInt(state.theField.fileValue?.sizeInBytes) || 0;

        let fileName = fileSize > 0 ? state.theField?.label : '';

        if (fileName === 'P45 form') {
          fileName = t('hr-field-view:P45-form-uploaded');
        }

        if (fileName === SUPER_CHOICE_FORM_LABEL) {
          fileName = t('hr-field-view:uploaded');
        }

        return fileName || notUploadedText;
      }
      if (state.theField.type === 'DOCSIGNATURE_REQUEST') {
        return t(
          checkSignatureStatus(
            state.theField.docSignatureEmbedValue.providerData
          )
            ? 'hr-field-view:completed'
            : 'hr-field-view:pending'
        );
      }
      if (!isRequiredField && !state.theField.value) {
        return t('hr-field-view:not-provided');
      }

      return state.theField.validation && state.theField.validation.formatter
        ? state.theField.validation.formatter(state.theField.value)
        : state.theField.value;
    });

    function maskString(str) {
      if (str.length <= 3) {
        return str;
      }

      const lastThree = str.slice(-3);
      const maskedPart = '*'.repeat(str.length - 3);

      return `${maskedPart}${lastThree}`;
    }

    const sensitiveMasking = computed(() => {
      if (!state.theField.validation.sensitive) {
        return formattedValue.value;
      }

      if (
        [
          t('hr-field-view:not-provided'),
          t('hr-field-view:not-uploaded'),
        ].includes(formattedValue.value)
      ) {
        return '*****';
      }

      return maskString(formattedValue.value);
    });

    function toggleMask() {
      if (isPortalUser) {
        return;
      }

      state.viewSensitive = !state.viewSensitive;
    }

    async function initialize() {
      let type = await typeTranslation(state.theField.type);

      if (type.validation) {
        const fieldValidation = await getTypeValidator(type.validation);
        state.theField.validation = Object.assign(
          {},
          state.theField.validation,
          fieldValidation
        );

        if (fieldValidation.type) {
          type.type = fieldValidation.type;
        }
      }

      if (state.theField.appliedRules) {
        Object.keys(state.theField.appliedRules).forEach(
          (rule) => (state.rules[rule] = state.theField.appliedRules[rule])
        );
      }

      if (
        state.theField.type === 'LINK' ||
        state.theField.type === 'LINK_BUTTON'
      ) {
        state.rules.visible = false;
      }
    }
    initialize();

    return {
      t,
      state,
      shouldShow,
      formattedValue,
      toggleMask,
      sensitiveMasking,
      isPortalUser,
      testIdLabel,
      testIdValue,
    };
  },
};
</script>
